import type { MentionableEntity, MentionableEntityType } from '@cobliteam/hooks/src/useSearchStore';
import { useTranslation } from '@cobliteam/i18n';
import Command, { CommandEmpty, CommandGroup, CommandItem, CommandList } from '@cobliteam/ui/components/Command';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import Mention from '@tiptap/extension-mention';
import type { Editor } from '@tiptap/react';
import { EditorContent, Extension, NodeViewWrapper, type ReactNodeViewProps, ReactNodeViewRenderer, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { exitSuggestion } from '@tiptap/suggestion';
import Fuse from 'fuse.js';
import type { ComponentProps } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import tippy, { type Instance } from 'tippy.js';
import {
  type AIMentionNodeAttributes,
  ALL_MENTIONABLE_ENTITY_TYPES,
  buildMentionNodeAttributes,
  buildMentionSearchItem,
  getMentionEntityKey,
  getMentionEntityKeyFromAttributes,
  getMentionEntityTypeLabel,
  type MentionSearchItem,
  normalizeMentionText,
} from './AIEntityMentionsInput.utils';
import { PromptInputEditor } from './elements/PromptInput';

type PromptInputEditorProps = ComponentProps<typeof PromptInputEditor>;

interface AIEntityMentionsInputProps extends Omit<PromptInputEditorProps, 'children'> {
  value: string;
  entities: MentionableEntity[];
  selectedEntities: MentionableEntity[];
  onSelectedEntitiesChange: (entities: MentionableEntity[]) => void;
  onValueChange: (value: string) => void;
  isLoadingEntities?: boolean;
  hasMentionError?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}

export interface AIEntityMentionsInputHandle {
  focus: () => void;
  insertMentionTrigger: () => void;
}

type MentionCategoryMeta = {
  icon: string;
  iconBadgeClassName: string;
  iconClassName: string;
  label: string;
  singular: string;
};

type MentionCategoryRow = {
  count: number;
  icon: string;
  label: string;
  type: MentionableEntityType;
};

type MentionSuggestionMenuProps = {
  categoryMeta: Record<MentionableEntityType, MentionCategoryMeta>;
  command: (item: MentionSearchItem) => void;
  isLoadingEntities: boolean;
  items: MentionSearchItem[];
  query: string;
};

type MentionSuggestionMenuHandle = {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
};

export const MENTIONS_DISABLED = true;

const MAX_VISIBLE_RESULTS = 8;
const ENTITY_TYPE_ICON_NAMES: Record<MentionableEntityType, IconNames> = {
  vehicles: 'truck',
  drivers: 'user',
  devices: 'router',
  vehicleGroups: 'cb-filters-vehicle-group-cb',
  geofences: 'map-pin',
};
const ENTITY_TYPE_ICON_CLASS_NAMES: Record<MentionableEntityType, string> = {
  vehicles: 'text-primary-10',
  drivers: 'text-warning-10',
  devices: 'text-accessory-purple-10',
  vehicleGroups: 'text-success-10',
  geofences: 'text-accessory-sky-10',
};

const buildEditorDocumentFromText = (value: string) => {
  const lines = value.split('\n');
  const content = lines.flatMap((line, index) => {
    const nodes: Array<Record<string, unknown>> = [];

    if (line) {
      nodes.push({ type: 'text', text: line });
    }

    if (index < lines.length - 1) {
      nodes.push({ type: 'hardBreak' });
    }

    return nodes;
  });

  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content,
      },
    ],
  };
};

const areEntityListsEqual = (left: MentionableEntity[], right: MentionableEntity[]) => {
  if (left.length !== right.length) return false;

  return left.every((entity, index) => getMentionEntityKey(entity) === getMentionEntityKey(right[index]));
};

const createSubmitBehaviorExtension = (suggestionMenuOpenRef: { current: boolean }) =>
  Extension.create({
    name: 'aiPromptSubmitBehavior',
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          if (suggestionMenuOpenRef.current) {
            return false;
          }

          const form = this.editor.view.dom.closest('form');
          if (!(form instanceof HTMLFormElement)) {
            return false;
          }

          form.requestSubmit();
          return true;
        },
        'Shift-Enter': () => this.editor.commands.setHardBreak(),
      };
    },
  });

const getSuggestionClientRect = (clientRect?: (() => DOMRect | null) | null) => {
  return () => clientRect?.() ?? new DOMRect();
};

const MentionChipView = ({ node }: ReactNodeViewProps<HTMLSpanElement>) => {
  const entityType = (node.attrs.entityType as MentionableEntityType | null) ?? 'vehicles';

  return (
    <NodeViewWrapper as='span' className={cn('mention-chip', `mention-chip--${entityType}`)} data-type='mention' contentEditable={false}>
      <span className='mention-chip-icon' aria-hidden>
        <Icon name={ENTITY_TYPE_ICON_NAMES[entityType]} className={cn('size-3.5', ENTITY_TYPE_ICON_CLASS_NAMES[entityType])} />
      </span>
      <span className='mention-chip-label'>{String(node.attrs.label ?? node.attrs.id ?? '')}</span>
    </NodeViewWrapper>
  );
};

const EntityMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      entityType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-entity-type'),
        renderHTML: (attributes) => {
          if (!attributes.entityType) return {};

          return {
            'data-entity-type': attributes.entityType,
          };
        },
      },
      kind: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-kind'),
        renderHTML: (attributes) => {
          if (!attributes.kind) return {};

          return {
            'data-kind': attributes.kind,
          };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(MentionChipView);
  },
});

const MentionSuggestionMenu = forwardRef<MentionSuggestionMenuHandle, MentionSuggestionMenuProps>(({ categoryMeta, command, isLoadingEntities, items, query }, ref) => {
  const [activeCategory, setActiveCategory] = useState<MentionableEntityType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const hasTypedQuery = Boolean(normalizeMentionText(query));

  const categoryRows = useMemo<MentionCategoryRow[]>(() => {
    return ALL_MENTIONABLE_ENTITY_TYPES.map((entityType) => ({
      type: entityType,
      label: categoryMeta[entityType].label,
      icon: categoryMeta[entityType].icon,
      count: items.filter((item) => item.entityType === entityType).length,
    })).filter((row) => row.count > 0 || !hasTypedQuery);
  }, [categoryMeta, hasTypedQuery, items]);

  const visibleItems = useMemo(() => {
    if (!activeCategory) {
      return hasTypedQuery ? items.slice(0, MAX_VISIBLE_RESULTS) : [];
    }

    return items.filter((item) => item.entityType === activeCategory).slice(0, MAX_VISIBLE_RESULTS);
  }, [activeCategory, hasTypedQuery, items]);

  const rowCount = activeCategory ? visibleItems.length : hasTypedQuery ? visibleItems.length : categoryRows.length;
  const resolvedActiveIndex = rowCount === 0 ? 0 : Math.min(activeIndex, rowCount - 1);

  useEffect(() => {
    if (!hasTypedQuery) return;
    setActiveCategory(null);
    setActiveIndex(0);
  }, [hasTypedQuery]);

  const selectActiveRow = useCallback(() => {
    if (activeCategory || hasTypedQuery) {
      const selectedItem = visibleItems[resolvedActiveIndex];
      if (selectedItem) {
        command(selectedItem);
      }
      return true;
    }

    const selectedCategory = categoryRows[resolvedActiveIndex];
    if (selectedCategory?.count) {
      setActiveCategory(selectedCategory.type);
      setActiveIndex(0);
      return true;
    }

    return false;
  }, [activeCategory, categoryRows, command, hasTypedQuery, resolvedActiveIndex, visibleItems]);

  const navigate = useCallback(
    (direction: 1 | -1) => {
      if (rowCount === 0) return;
      setActiveIndex((currentIndex) => (currentIndex + direction + rowCount) % rowCount);
    },
    [rowCount]
  );

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          navigate(event.key === 'ArrowDown' ? 1 : -1);
          return true;
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          return selectActiveRow();
        }

        if (event.key === 'Escape' && activeCategory) {
          event.preventDefault();
          setActiveCategory(null);
          return true;
        }

        return false;
      },
    }),
    [activeCategory, navigate, selectActiveRow]
  );

  return (
    <div className='w-[min(22rem,calc(100vw-2rem))] rounded-[10px] border border-gray-4 bg-white p-1 shadow-[0_22px_48px_rgba(15,23,42,0.16),0_8px_18px_rgba(15,23,42,0.10)]'>
      <div className='border-gray-4 border-b px-2 py-2'>
        <div className='flex items-center gap-1.5 px-0.5'>
          {activeCategory && (
            <button
              type='button'
              className='flex size-4 items-center justify-center rounded-sm text-gray-9 transition-colors hover:bg-gray-2 hover:text-gray-11'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setActiveCategory(null);
                setActiveIndex(0);
              }}
            >
              <Icon name='chevron-left' className='size-2.5' />
            </button>
          )}
          <span className='font-semibold text-gray-11 text-xs uppercase tracking-[0.06em]'>
            {activeCategory ? `Buscar ${categoryMeta[activeCategory].label.toLowerCase()}` : hasTypedQuery ? `Resultados para @${query}` : 'Adicionar menção'}
          </span>
        </div>
      </div>

      <Command shouldFilter={false} className='rounded-[12px]'>
        <CommandList className='max-h-64 p-1'>
          {isLoadingEntities ? (
            <div className='flex items-center gap-1.5 px-2 py-2 text-gray-9 text-xs'>
              <Icon name='loader-circle' className='size-3 animate-spin' />
              Carregando entidades...
            </div>
          ) : activeCategory || hasTypedQuery ? (
            <CommandGroup>
              {visibleItems.map((item, index) => (
                <CommandItem
                  key={item.key}
                  value={item.key}
                  className={cn('items-start justify-between rounded-md px-2 py-1.5', resolvedActiveIndex === index && 'bg-gray-4 shadow-[inset_0_0_0_1px_rgba(203,213,225,0.65)]')}
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => command(item)}
                >
                  <div className='flex min-w-0 items-start gap-1.5'>
                    <span className={cn('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md', categoryMeta[item.entityType].iconBadgeClassName)}>
                      <Icon name={categoryMeta[item.entityType].icon as IconNames} className={cn('size-3', categoryMeta[item.entityType].iconClassName)} />
                    </span>
                    <div className='flex min-w-0 flex-col gap-0'>
                      {hasTypedQuery && !activeCategory && (
                        <span className='font-medium text-gray-8 text-xs uppercase tracking-[0.05em]'>{categoryMeta[item.entityType].singular}</span>
                      )}
                      <span className='truncate font-medium text-gray-12 text-xs'>{item.primary}</span>
                    </div>
                  </div>
                  {item.secondary && <span className='max-w-28 truncate pl-2 text-right font-medium text-gray-10 text-xs'>{item.secondary}</span>}
                </CommandItem>
              ))}
              {visibleItems.length === 0 && <CommandEmpty className='py-4 text-xs'>Nenhum resultado encontrado.</CommandEmpty>}
            </CommandGroup>
          ) : (
            <CommandGroup>
              {categoryRows.map((row, index) => (
                <CommandItem
                  key={row.type}
                  value={row.type}
                  className={cn(
                    'justify-between rounded-md px-2 py-1.5 text-xs',
                    resolvedActiveIndex === index && 'bg-gray-3 shadow-[inset_0_0_0_1px_rgba(203,213,225,0.65)]',
                    row.count === 0 && 'pointer-events-none opacity-50'
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => {
                    if (!row.count) return;
                    setActiveCategory(row.type);
                    setActiveIndex(0);
                  }}
                >
                  <div className='flex items-center gap-1.5'>
                    <span className={cn('flex size-5 shrink-0 items-center justify-center rounded-md', categoryMeta[row.type].iconBadgeClassName)}>
                      <Icon name={row.icon as IconNames} className={cn('size-3', categoryMeta[row.type].iconClassName)} />
                    </span>
                    <span className='font-medium text-gray-11 text-xs'>{row.label}</span>
                  </div>
                  <span className='font-medium text-gray-9 text-xs'>{row.count}</span>
                </CommandItem>
              ))}
              {categoryRows.length === 0 && <CommandEmpty className='py-4 text-xs'>Nenhum resultado encontrado.</CommandEmpty>}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </div>
  );
});

MentionSuggestionMenu.displayName = 'MentionSuggestionMenu';

const AIEntityMentionsInput = forwardRef<AIEntityMentionsInputHandle, AIEntityMentionsInputProps>(
  (
    {
      autoFocus = false,
      className,
      value,
      entities,
      selectedEntities,
      onSelectedEntitiesChange,
      onValueChange,
      isLoadingEntities = false,
      hasMentionError = false,
      disabled,
      placeholder = 'O que você deseja saber?',
      minHeight,
      maxHeight,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('global-search');
    const [isEditorEmpty, setIsEditorEmpty] = useState(!value.trim() && selectedEntities.length === 0);
    const suggestionMenuOpenRef = useRef(false);
    const disabledRef = useRef(disabled);
    const entitiesRef = useRef(entities);
    const selectedEntitiesRef = useRef(selectedEntities);
    const valueRef = useRef(value);
    const onValueChangeRef = useRef(onValueChange);
    const onSelectedEntitiesChangeRef = useRef(onSelectedEntitiesChange);
    const isLoadingEntitiesRef = useRef(isLoadingEntities);
    const hasMentionErrorRef = useRef(hasMentionError);
    const mentionItemsRef = useRef<MentionSearchItem[]>([]);
    const mentionFuseRef = useRef<Fuse<MentionSearchItem> | null>(null);

    const categoryMeta = useMemo<Record<MentionableEntityType, MentionCategoryMeta>>(
      () => ({
        vehicles: { icon: 'truck', label: t('sidebar.vehicles'), singular: 'Veiculo', iconClassName: 'text-primary', iconBadgeClassName: 'bg-primary-2' },
        drivers: { icon: 'user', label: t('sidebar.drivers'), singular: 'Motorista', iconClassName: 'text-warning', iconBadgeClassName: 'bg-warning-2' },
        devices: { icon: 'router', label: t('sidebar.devices'), singular: 'Dispositivo', iconClassName: 'text-accessory-purple-9', iconBadgeClassName: 'bg-accessory-purple-2' },
        vehicleGroups: { icon: 'cb-filters-vehicle-group-cb', label: t('sidebar.groups'), singular: 'Grupo', iconClassName: 'text-success', iconBadgeClassName: 'bg-success-2' },
        geofences: { icon: 'map-pin', label: t('sidebar.places_of_interest'), singular: 'Cerca', iconClassName: 'text-accessory-sky-11', iconBadgeClassName: 'bg-accessory-sky-2' },
      }),
      [t]
    );

    const mentionItems = useMemo(() => entities.map(buildMentionSearchItem), [entities]);

    const mentionFuse = useMemo(() => {
      return new Fuse(mentionItems, {
        ignoreLocation: true,
        threshold: 0.3,
        getFn: (item, path) => {
          const foundValue = Fuse.config.getFn(item, path);

          if (typeof foundValue === 'string') {
            return normalizeMentionText(foundValue);
          }

          if (Array.isArray(foundValue)) {
            return foundValue.map((entry) => (typeof entry === 'string' ? normalizeMentionText(entry) : entry));
          }

          return foundValue;
        },
        keys: [
          { name: 'primary', weight: 2 },
          { name: 'secondary', weight: 1 },
          { name: 'searchValues', weight: 1.5 },
        ],
      });
    }, [mentionItems]);

    useEffect(() => {
      disabledRef.current = disabled;
    }, [disabled]);

    useEffect(() => {
      entitiesRef.current = entities;
    }, [entities]);

    useEffect(() => {
      selectedEntitiesRef.current = selectedEntities;
    }, [selectedEntities]);

    useEffect(() => {
      valueRef.current = value;
    }, [value]);

    useEffect(() => {
      onValueChangeRef.current = onValueChange;
    }, [onValueChange]);

    useEffect(() => {
      onSelectedEntitiesChangeRef.current = onSelectedEntitiesChange;
    }, [onSelectedEntitiesChange]);

    useEffect(() => {
      isLoadingEntitiesRef.current = isLoadingEntities;
    }, [isLoadingEntities]);

    useEffect(() => {
      hasMentionErrorRef.current = hasMentionError;
    }, [hasMentionError]);

    useEffect(() => {
      mentionItemsRef.current = mentionItems;
      mentionFuseRef.current = mentionFuse;
    }, [mentionFuse, mentionItems]);

    const syncEditorState = useCallback((editorInstance: Editor) => {
      const nextValue = editorInstance.getText({
        blockSeparator: '\n',
        textSerializers: {
          mention: ({ node }) => `@${String(node.attrs.label ?? node.attrs.id ?? '').trim()}`,
        },
      });

      const nextEntities: MentionableEntity[] = [];
      const nextEntityKeys = new Set<string>();

      editorInstance.state.doc.descendants((node) => {
        if (node.type.name !== 'mention') return;

        const attributes = node.attrs as Partial<AIMentionNodeAttributes>;
        if (!(attributes.id && attributes.kind)) return;

        const entityKey = getMentionEntityKeyFromAttributes({
          id: attributes.id,
          kind: attributes.kind,
        });

        if (nextEntityKeys.has(entityKey)) return;

        const matchingEntity =
          entitiesRef.current.find((entity) => getMentionEntityKey(entity) === entityKey) ??
          selectedEntitiesRef.current.find((entity) => getMentionEntityKey(entity) === entityKey);

        if (!matchingEntity) return;

        nextEntityKeys.add(entityKey);
        nextEntities.push(matchingEntity);
      });

      setIsEditorEmpty(!nextValue.trim() && nextEntities.length === 0);

      if (nextValue !== valueRef.current) {
        onValueChangeRef.current(nextValue);
      }

      if (!areEntityListsEqual(nextEntities, selectedEntitiesRef.current)) {
        onSelectedEntitiesChangeRef.current(nextEntities);
      }
    }, []);

    const getSuggestionItems = useCallback((query: string) => {
      const normalizedQuery = normalizeMentionText(query);
      const source = mentionItemsRef.current;

      if (!normalizedQuery) {
        return source;
      }

      return mentionFuseRef.current?.search(normalizedQuery).map((result) => result.item) ?? [];
    }, []);

    const submitBehaviorExtension = useMemo(() => createSubmitBehaviorExtension(suggestionMenuOpenRef), []);

    const editor = useEditor({
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      content: buildEditorDocumentFromText(value),
      editable: !disabled,
      extensions: [
        StarterKit.configure({
          blockquote: false,
          bold: false,
          bulletList: false,
          code: false,
          codeBlock: false,
          dropcursor: false,
          gapcursor: false,
          heading: false,
          horizontalRule: false,
          italic: false,
          orderedList: false,
          strike: false,
          underline: false,
        }),
        submitBehaviorExtension,
        EntityMention.configure({
          HTMLAttributes: {
            class: 'mention-chip',
          },
          deleteTriggerWithBackspace: true,
          renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id ?? ''}`,
          renderHTML: ({ options, node }) => {
            const entityType = (node.attrs.entityType as MentionableEntityType | undefined) ?? 'vehicles';
            const entityTypeLabel = getMentionEntityTypeLabel(entityType);

            return [
              'span',
              {
                ...options.HTMLAttributes,
                'data-type': 'mention',
                'data-entity-type': entityType,
                'data-kind': node.attrs.kind ?? '',
                class: `mention-chip mention-chip--${entityType}`,
              },
              ['span', { class: 'mention-chip-type' }, entityTypeLabel],
              ['span', { class: 'mention-chip-label' }, `${node.attrs.label ?? node.attrs.id ?? ''}`],
            ];
          },
          suggestion: {
            allowedPrefixes: [' ', '\n'],
            allow: () => !(MENTIONS_DISABLED || hasMentionErrorRef.current || disabledRef.current),
            char: '@',
            command: ({ editor: editorInstance, range, props: mentionAttributes }) => {
              editorInstance
                .chain()
                .focus()
                .insertContentAt(range, [
                  {
                    type: 'mention',
                    attrs: mentionAttributes,
                  },
                  {
                    type: 'text',
                    text: ' ',
                  },
                ])
                .run();
            },
            items: ({ query }) => getSuggestionItems(query),
            render: () => {
              let popup: Instance | null = null;
              let component: ReactRenderer<MentionSuggestionMenuHandle, MentionSuggestionMenuProps> | null = null;

              return {
                onStart: (suggestionProps: SuggestionProps<MentionSearchItem, AIMentionNodeAttributes>) => {
                  suggestionMenuOpenRef.current = true;

                  component = new ReactRenderer(MentionSuggestionMenu, {
                    editor: suggestionProps.editor,
                    props: {
                      categoryMeta,
                      command: (item: MentionSearchItem) => suggestionProps.command(buildMentionNodeAttributes(item.entity)),
                      isLoadingEntities: isLoadingEntitiesRef.current,
                      items: suggestionProps.items,
                      query: suggestionProps.query,
                    },
                  });

                  if (!suggestionProps.clientRect) {
                    return;
                  }

                  popup = tippy(document.body, {
                    appendTo: () => document.body,
                    arrow: false,
                    content: component.element,
                    duration: 0,
                    getReferenceClientRect: getSuggestionClientRect(suggestionProps.clientRect),
                    interactive: true,
                    maxWidth: 'none',
                    offset: [0, 8],
                    placement: 'top-start',
                    showOnCreate: true,
                    trigger: 'manual',
                  });
                },
                onUpdate: (suggestionProps: SuggestionProps<MentionSearchItem, AIMentionNodeAttributes>) => {
                  component?.updateProps({
                    categoryMeta,
                    command: (item: MentionSearchItem) => suggestionProps.command(buildMentionNodeAttributes(item.entity)),
                    isLoadingEntities: isLoadingEntitiesRef.current,
                    items: suggestionProps.items,
                    query: suggestionProps.query,
                  });

                  if (!suggestionProps.clientRect) {
                    return;
                  }

                  popup?.setProps({
                    getReferenceClientRect: getSuggestionClientRect(suggestionProps.clientRect),
                  });
                },
                onKeyDown: (suggestionKeyDownProps: SuggestionKeyDownProps) => {
                  const handledByMenu = component?.ref?.onKeyDown(suggestionKeyDownProps) ?? false;

                  if (handledByMenu) {
                    return true;
                  }

                  if (suggestionKeyDownProps.event.key === 'Escape') {
                    exitSuggestion(suggestionKeyDownProps.view);
                    popup?.hide();
                    return true;
                  }

                  return false;
                },
                onExit: () => {
                  suggestionMenuOpenRef.current = false;
                  popup?.destroy();
                  component?.destroy();
                },
              };
            },
          },
        }),
      ],
      editorProps: {
        attributes: {
          class: 'min-h-full w-full bg-white p-2 text-sm leading-5 text-gray-12 outline-none',
          role: 'textbox',
          'aria-multiline': 'true',
        },
      },
      onCreate: ({ editor: editorInstance }) => {
        syncEditorState(editorInstance);
      },
      onUpdate: ({ editor: editorInstance }) => {
        syncEditorState(editorInstance);
      },
    });

    useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [disabled, editor]);

    useEffect(() => {
      if (!(editor && autoFocus) || disabled) return;
      editor.commands.focus('end');
    }, [autoFocus, disabled, editor]);

    useEffect(() => {
      if (!editor) return;

      const editorText = editor.getText({
        blockSeparator: '\n',
        textSerializers: {
          mention: ({ node }) => `@${String(node.attrs.label ?? node.attrs.id ?? '').trim()}`,
        },
      });

      if (value === editorText) {
        return;
      }

      if (!value && selectedEntities.length === 0) {
        editor.commands.clearContent(false);
        setIsEditorEmpty(true);
        return;
      }

      if (selectedEntities.length === 0) {
        editor.commands.setContent(buildEditorDocumentFromText(value), { emitUpdate: false });
        setIsEditorEmpty(!value.trim());
      }
    }, [editor, selectedEntities.length, value]);

    const handleInsertMentionTrigger = useCallback(() => {
      if (!editor || disabled || hasMentionError) return;

      const { from } = editor.state.selection;
      const previousCharacter = editor.state.doc.textBetween(Math.max(0, from - 1), from, '\n');
      const needsLeadingSpace = Boolean(previousCharacter) && !/\s/.test(previousCharacter);

      editor
        .chain()
        .focus()
        .insertContent(needsLeadingSpace ? ' @' : '@')
        .run();
    }, [disabled, editor, hasMentionError]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.commands.focus('end'),
        insertMentionTrigger: handleInsertMentionTrigger,
      }),
      [editor, handleInsertMentionTrigger]
    );

    return (
      <PromptInputEditor
        {...props}
        className={cn('cursor-text', disabled && 'cursor-not-allowed opacity-60', className)}
        maxHeight={maxHeight}
        minHeight={minHeight}
        onClick={() => editor?.commands.focus('end')}
      >
        {isEditorEmpty && <span className='pointer-events-none absolute top-2 left-3.5 z-10 text-gray-8 text-sm leading-5'>{placeholder}</span>}
        <EditorContent editor={editor} />
      </PromptInputEditor>
    );
  }
);

AIEntityMentionsInput.displayName = 'AIEntityMentionsInput';

export default AIEntityMentionsInput;
